import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Utensils, ShoppingBag, ReceiptText, ChevronRight, Plus, Minus, Menu, X, Users, Tag, PiggyBank, PartyPopper } from 'lucide-react';

const SLIDES = [
  {
    image: '/images/forEveryGroup.PNG',
    title: 'Join a Group',
    subtitle: 'Discover and join groups with shared interests in a product or service',
    containImage: false,
    icon: Users,
  },
  {
    image: '/images/rewardsReceived.PNG',
    title: 'Enjoy Exclusive Deals',
    subtitle: 'Get access to exclusive deals and cashback rewards curated for your groups',
    containImage: false,
    icon: Tag,
  },
  {
    image: '/images/save on bills and meals.PNG',
    title: 'Save on Daily Expenses',
    subtitle: 'Pay merchants with your rewards and put money back in your pocket',
    containImage: false,
    icon: PiggyBank,
  },
  {
    image: '/images/chat, celebrate.PNG',
    title: 'Win Together',
    subtitle: 'Celebrate group wins and inspire each other to achieve more together',
    containImage: true,
    icon: PartyPopper,
  },
];

// Card stack positions for mobile cycling (3 cards)
const CARD_POSITIONS = [
  { rot: '-8deg', ty: '-18px', scale: '0.96', shadow: '0 8px 24px rgba(0,0,0,.10)', z: 1 },
  { rot: '5deg', ty: '-10px', scale: '0.98', shadow: '0 12px 32px rgba(0,0,0,.12)', z: 2 },
  { rot: '0deg', ty: '0px', scale: '1', shadow: '0 24px 56px rgba(0,0,0,.18)', z: 3 },
];

const HERO_WORDS = ['Pay', 'Shop'];

export default function LandingPage() {
  const navigate = useNavigate();
  const [activeCard, setActiveCard] = useState(1); // Start with BillSavers (index 1, center card) on top
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const cardTimerRef = useRef(null);
  const [faqItems, setFaqItems] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [displayText, setDisplayText] = useState('Pay');
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Typewriter effect
  useEffect(() => {
    const currentWord = HERO_WORDS[wordIndex];
    const typeSpeed = isDeleting ? 80 : 120;

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          // Finished typing, wait then start deleting
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        // Deleting
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          // Finished deleting, move to next word
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % HERO_WORDS.length);
        }
      }
    }, typeSpeed);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, wordIndex]);

  const handleJoinBonndle = () => {
    navigate('/welcome');
  };

  const handleSignIn = () => {
    navigate('/signin');
  };

  // Card stack cycling - mobile only
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 860;
      setIsMobile(mobile);

      if (mobile) {
        // Start card cycling on mobile
        if (!cardTimerRef.current) {
          cardTimerRef.current = setInterval(() => {
            setActiveCard((prev) => (prev + 1) % 3);
          }, 5000);
        }
      } else {
        // Clear timer on desktop
        if (cardTimerRef.current) {
          clearInterval(cardTimerRef.current);
          cardTimerRef.current = null;
        }
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
      if (cardTimerRef.current) {
        clearInterval(cardTimerRef.current);
      }
    };
  }, []);

  // Get card style based on position in stack
  const getCardStyle = (cardIndex) => {
    if (!isMobile) return {};

    const slot = (cardIndex - activeCard + 3) % 3;
    const pos = CARD_POSITIONS[slot];

    return {
      transform: `translateX(-50%) rotate(${pos.rot}) translateY(${pos.ty}) scale(${pos.scale})`,
      boxShadow: pos.shadow,
      zIndex: pos.z,
    };
  };

  // Fetch FAQ items
  useEffect(() => {
    const fetchFaq = async () => {
      try {
        const response = await fetch(
          'https://boldscaleservices.wixsite.com/workspaces/_functions/bonndleFAQ',
          {
            headers: {
              'x-api-key': 'bsclw1x@pisvc_90uhqwd0164hb1307913hhj92743r2u49uniu2h3gr99_',
            },
          }
        );
        const data = await response.json();
        if (data.items) {
          setFaqItems(data.items.filter(item => item.isActive).sort((a, b) => a.order - b.order));
        }
      } catch (error) {
        console.error('Failed to fetch FAQ:', error);
      }
    };
    fetchFaq();
  }, []);

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  return (
    <div className="landing-page">
      {/* NAV */}
      <nav className="landing-nav">
        <a href="/" className="landing-nav-logo">
          <svg className="landing-nav-logo-mark" width="28" height="28" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <rect width="64" height="64" rx="14" fill="#4F46E5"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M31.9881 18.2238C29.9158 18.2238 27.9284 19.047 26.463 20.5125C24.9977 21.978 24.1744 23.9656 24.1744 26.038C24.1744 28.1105 24.9977 30.0981 26.463 31.5636C27.9284 33.029 29.9158 33.8523 31.9881 33.8523C34.0604 33.8523 36.0479 33.029 37.5132 31.5636C38.9786 30.0981 39.8018 28.1105 39.8018 26.038C39.8018 23.9656 38.9786 21.978 37.5132 20.5125C36.0479 19.047 34.0604 18.2238 31.9881 18.2238ZM21.942 26.038C21.9432 24.6093 22.2492 23.1972 22.8395 21.896C23.4297 20.5949 24.2907 19.4346 25.3649 18.4927C26.4391 17.5507 27.7019 16.8487 29.0689 16.4335C30.4359 16.0183 31.8758 15.8995 33.2924 16.085C34.7089 16.2705 36.0697 16.756 37.2837 17.5092C38.4978 18.2623 39.5373 19.2658 40.3328 20.4526C41.1283 21.6394 41.6615 22.9822 41.897 24.3915C42.1324 25.8007 42.0645 27.244 41.6979 28.6249C43.0218 29.157 44.2172 29.9649 45.2046 30.9948C46.1921 32.0248 46.9489 33.2532 47.4247 34.5984C47.9005 35.9436 48.0845 37.3747 47.9643 38.7965C47.8441 40.2184 47.4226 41.5983 46.7277 42.8445C46.0329 44.0907 45.0806 45.1747 43.9344 46.0243C42.7881 46.8739 41.474 47.4697 40.0796 47.7719C38.6852 48.0742 37.2425 48.076 35.8473 47.7773C34.4521 47.4787 33.1366 46.8863 31.9881 46.0396C30.8396 46.8822 29.5251 47.4708 28.1318 47.7665C26.7384 48.0623 25.2982 48.0583 23.9065 47.7549C22.5148 47.4515 21.2036 46.8556 20.0597 46.0068C18.9159 45.158 17.9656 44.0756 17.2719 42.8315C16.5782 41.5873 16.157 40.2099 16.0362 38.7906C15.9154 37.3712 16.0978 35.9424 16.5712 34.599C17.0447 33.2555 17.7984 32.028 18.7823 30.9981C19.7662 29.9681 20.9579 29.1592 22.2783 28.6249C22.0551 27.7808 21.942 26.9112 21.942 26.038ZM23.088 30.7058C21.4115 31.3906 20.0248 32.6364 19.1648 34.2302C18.3049 35.8241 18.0249 37.6671 18.3728 39.4444C18.7207 41.2218 19.6749 42.8231 21.0724 43.975C22.4699 45.1269 24.2239 45.7577 26.0348 45.7598C27.2306 45.7602 28.4106 45.4862 29.4838 44.9587C30.557 44.4313 31.4948 43.6645 32.225 42.7176C32.9553 41.7706 33.4585 40.6686 33.6958 39.4965C33.9331 38.3244 33.8983 37.1135 33.594 35.957C31.5134 36.2941 29.3795 35.9683 27.4943 35.0257C25.6091 34.083 24.0681 32.5712 23.0895 30.7043M35.7461 35.3571C35.9664 36.1832 36.0825 37.051 36.0825 37.944C36.0865 40.3431 35.2282 42.6638 33.664 44.4827C34.5758 45.0761 35.6015 45.4725 36.6753 45.6465C37.7491 45.8206 38.8475 45.7685 39.9 45.4935C40.9526 45.2186 41.9361 44.7269 42.7877 44.0499C43.6392 43.3729 44.3401 42.5255 44.8453 41.562C45.3505 40.5986 45.649 39.5402 45.7216 38.4547C45.7943 37.3692 45.6395 36.2804 45.2671 35.2582C44.8948 34.236 44.3131 33.3028 43.5593 32.5184C42.8055 31.7339 41.8963 31.1155 40.8898 30.7028C39.779 32.8134 37.9568 34.4623 35.7461 35.3571Z" fill="white"/>
          </svg>
          <span className="landing-nav-wordmark">bonndle</span>
        </a>
        <div className="landing-nav-links">
          <a href="#features" className="landing-nav-link">How it Works</a>
          <a
            href="https://play.google.com/store/apps/details?id=boldscale.io.bold_explorer"
            target="_blank"
            rel="noopener noreferrer"
            className="landing-nav-link"
          >
            Get Started
          </a>
          <a
            href="mailto:support@bonndle.com?subject=Bonndle%20Support%20Request"
            className="landing-nav-link"
          >
            Support
          </a>
        </div>
        <button
          className="landing-nav-burger"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="landing-mobile-menu-backdrop" onClick={() => setMobileMenuOpen(false)} />}
      <div className={`landing-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <button
          className="landing-mobile-menu-close"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close menu"
        >
          <X size={24} />
        </button>
        <div className="landing-mobile-menu-content">
          <a href="#features" className="landing-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
            <span>How it Works</span>
            <ChevronRight size={20} />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=boldscale.io.bold_explorer"
            target="_blank"
            rel="noopener noreferrer"
            className="landing-mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span>Get Started</span>
            <ChevronRight size={20} />
          </a>
          <a
            href="mailto:support@bonndle.com?subject=Bonndle%20Support%20Request"
            className="landing-mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span>Support</span>
            <ChevronRight size={20} />
          </a>
          <Link to="/about" className="landing-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>
            <span>About</span>
            <ChevronRight size={20} />
          </Link>
        </div>
      </div>

      {/* HERO */}
      <section className="landing-hero landing-canvas-bg">
        <div className="landing-aurora"></div>
        <div className="landing-hero-inner">
          <h1 className="landing-hero-headline">
            <span className="hero-word-typewriter">{displayText}</span> <em>Together.</em><br />Save More.
          </h1>
          <p className="landing-hero-sub">
            Spend less on bills, meals and more with group cashback rewards
          </p>

          {/* Card Stack */}
          <div className="landing-card-stack-wrap" aria-hidden="true">
            {/* Card 0: Gold - Left */}
            <div className="landing-stack-card landing-stack-card--gold" style={getCardStyle(0)}>
              <div className="landing-stack-card-top">
                <Utensils className="landing-stack-card-icon" />
                <div className="landing-stack-card-group">#FoodieSquad</div>
                <div className="landing-stack-card-label">₦24,000 saved this month</div>
              </div>
              <div className="landing-stack-card-bottom">
                <div className="landing-stack-card-cashback">30% <span>cashback</span></div>
                <div className="landing-stack-card-members">124 members</div>
              </div>
            </div>
            {/* Card 1: Indigo - Center (BillSavers) */}
            <div className="landing-stack-card landing-stack-card--indigo" style={getCardStyle(1)}>
              <div className="landing-stack-card-top">
                <ReceiptText className="landing-stack-card-icon" />
                <div className="landing-stack-card-group">#BillSavers</div>
                <div className="landing-stack-card-label">₦52,000 saved this month</div>
              </div>
              <div className="landing-stack-card-bottom">
                <div className="landing-stack-card-cashback">50% <span>cashback</span></div>
                <div className="landing-stack-card-members">212 members</div>
              </div>
            </div>
            {/* Card 2: Violet - Right */}
            <div className="landing-stack-card landing-stack-card--violet" style={getCardStyle(2)}>
              <div className="landing-stack-card-top">
                <ShoppingBag className="landing-stack-card-icon" />
                <div className="landing-stack-card-group">#WeekendShoppers</div>
                <div className="landing-stack-card-label">₦18,500 saved this month</div>
              </div>
              <div className="landing-stack-card-bottom">
                <div className="landing-stack-card-cashback">20% <span>cashback</span></div>
                <div className="landing-stack-card-members">89 members</div>
              </div>
            </div>
          </div>

          {/* Google Play Button */}
          <div className="landing-store-buttons-row">
            <a
              href="https://play.google.com/store/apps/details?id=boldscale.io.bold_explorer"
              target="_blank"
              rel="noopener noreferrer"
              className="landing-store-btn-dark"
            >
              <svg viewBox="0 0 24 24" className="landing-store-btn-icon">
                <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.25-.84-.76-.84-1.35m13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27m3.35-4.31c.34.27.56.69.56 1.19s-.22.92-.56 1.19l-2.56 1.53-2.83-2.83 2.83-2.83 2.56 1.75M6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/>
              </svg>
              <div className="landing-store-btn-text">
                <span className="landing-store-btn-label">Get it on</span>
                <span className="landing-store-btn-name">Google Play</span>
              </div>
            </a>
          </div>
          <p className="landing-ios-note">Coming to iOS soon</p>
        </div>
      </section>

      {/* FEATURES - 2 Column Grid */}
      <section className="landing-features-section" id="features">
        <h2 className="landing-features-header"><em>Unlimited Rewards</em></h2>
        <p className="landing-features-subtext">Built for every group - families, friends, teams and more</p>
        <div className="landing-features-grid">
          {SLIDES.map((slide, index) => {
            const IconComponent = slide.icon;
            return (
              <div key={index} className="landing-feature-card">
                <div className={`landing-feature-img-box ${slide.containImage ? 'landing-feature-img-box--contain' : ''}`}>
                  <div className="landing-feature-text">
                    <IconComponent size={32} className="landing-feature-icon" />
                    <h2 className="landing-feature-title">{slide.title}</h2>
                    <p className="landing-feature-sub">{slide.subtitle}</p>
                  </div>
                  <div className="landing-feature-img-wrap">
                    <img src={slide.image} alt={slide.title} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ Section */}
      {faqItems.length > 0 && (
        <section className="landing-faq-section" id="faq">
          <h2 className="landing-faq-title">Frequently Asked Questions</h2>
          <div className="landing-faq-list">
            {faqItems.map((item) => (
              <div
                key={item._id}
                className={`landing-faq-item ${expandedFaq === item._id ? 'expanded' : ''}`}
              >
                <button
                  className="landing-faq-question"
                  onClick={() => toggleFaq(item._id)}
                  aria-expanded={expandedFaq === item._id}
                >
                  <span>{item.question}</span>
                  {expandedFaq === item._id ? (
                    <Minus size={20} className="landing-faq-icon" />
                  ) : (
                    <Plus size={20} className="landing-faq-icon" />
                  )}
                </button>
                <div className="landing-faq-answer">
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="landing-cta-section">
        <div className="landing-cta-stack">
          <div className="landing-cta-box">
            <h2>Together is Better</h2>
            <div className="landing-store-buttons">
              <a
                href="https://play.google.com/store/apps/details?id=boldscale.io.bold_explorer"
                target="_blank"
                rel="noopener noreferrer"
                className="landing-store-btn"
              >
                <svg viewBox="0 0 24 24" className="landing-store-icon">
                  <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.25-.84-.76-.84-1.35m13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27m3.35-4.31c.34.27.56.69.56 1.19s-.22.92-.56 1.19l-2.56 1.53-2.83-2.83 2.83-2.83 2.56 1.75M6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/>
                </svg>
                <div className="landing-store-text">
                  <span className="landing-store-label">Get it on</span>
                  <span className="landing-store-name">Google Play</span>
                </div>
              </a>
            </div>
            <p className="landing-ios-note-cta">Coming to iOS soon</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <span className="landing-footer-copy">© 2026 bonndle. All Rights Reserved.</span>
        <span className="landing-footer-tagline">Better. Together.</span>
        <div className="landing-footer-right">
          <div className="landing-footer-links">
            <Link to="/about" className="landing-footer-link-btn">About</Link>
            <span className="landing-footer-dot">·</span>
            <a href="#">Terms</a>
            <span className="landing-footer-dot">·</span>
            <a href="#">Privacy</a>
          </div>
          <div className="landing-footer-contact">
            <a href="mailto:support@bonndle.com">support@bonndle.com</a>
            <span className="landing-footer-dot"> · </span>
            <a href="https://instagram.com/bonndleapp" target="_blank" rel="noopener noreferrer">@bonndleapp</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
